import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface FilterOption {
  value: string;
  label: string;
}

interface ColumnFilterDropdownProps {
  columnKey: string;
  label: string;
  options: string[] | FilterOption[];
  selected: string[];
  onChange: (columnKey: string, selected: string[]) => void;
  compact?: boolean;
  panelMinWidth?: number;
  panelMaxWidth?: number;
}

interface PanelPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
}

const PANEL_MAX_HEIGHT_PX = 224;
const PANEL_MIN_WIDTH_PX = 160;
const PANEL_MAX_WIDTH_PX = 256;

function normalizeOptions(options: string[] | FilterOption[]): FilterOption[] {
  return options.map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option,
  );
}

function optionValues(options: FilterOption[]): string[] {
  return options.map((option) => option.value);
}

function computePanelPosition(
  trigger: HTMLButtonElement,
  panelMinWidth = PANEL_MIN_WIDTH_PX,
  panelMaxWidth = PANEL_MAX_WIDTH_PX,
): PanelPosition {
  const rect = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUpward = spaceBelow < PANEL_MAX_HEIGHT_PX && rect.top > spaceBelow;
  const width = Math.min(panelMaxWidth, Math.max(panelMinWidth, rect.width));

  if (openUpward) {
    return {
      bottom: window.innerHeight - rect.top + 4,
      left: rect.left,
      width,
    };
  }

  return {
    top: rect.bottom + 4,
    left: rect.left,
    width,
  };
}

export function ColumnFilterDropdown({
  columnKey,
  label,
  options,
  selected,
  onChange,
  compact = false,
  panelMinWidth,
  panelMaxWidth,
}: ColumnFilterDropdownProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const normalized = normalizeOptions(options);

  const updatePanelPosition = useCallback((): void => {
    if (!triggerRef.current) {
      return;
    }
    setPanelPosition(computePanelPosition(triggerRef.current, panelMinWidth, panelMaxWidth));
  }, [panelMinWidth, panelMaxWidth]);

  useEffect(() => {
    if (!open) {
      setPanelPosition(null);
      return;
    }

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const summary =
    selected.length === 0
      ? 'All'
      : selected.length === 1
        ? normalized.find((option) => option.value === selected[0])?.label ?? '1 selected'
        : `${selected.length} selected`;

  const toggleOption = (value: string): void => {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    onChange(columnKey, next);
  };

  return (
    <div
      ref={rootRef}
      className={`bc-permissions-audit__col-filter-dropdown ${
        compact ? 'bc-permissions-audit__col-filter-dropdown--compact' : ''
      } ${selected.length > 0 ? 'bc-permissions-audit__col-filter-dropdown--active' : ''}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="bc-permissions-audit__col-filter-trigger"
        onClick={() => setOpen((previous) => !previous)}
        aria-label={`Filter ${label}`}
        aria-expanded={open}
      >
        <span className="bc-permissions-audit__col-filter-summary">{summary}</span>
        <span className="bc-permissions-audit__col-filter-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && panelPosition && (
        <div
          className="bc-permissions-audit__col-filter-panel bc-permissions-audit__col-filter-panel--fixed"
          role="group"
          aria-label={`${label} filter options`}
          style={{
            top: panelPosition.top,
            bottom: panelPosition.bottom,
            left: panelPosition.left,
            width: panelPosition.width,
          }}
        >
          <div className="bc-permissions-audit__col-filter-panel-actions">
            <button
              type="button"
              onClick={() => onChange(columnKey, optionValues(normalized))}
            >
              Select all
            </button>
            <button type="button" onClick={() => onChange(columnKey, [])}>
              Clear
            </button>
          </div>
          <ul className="bc-permissions-audit__col-filter-options">
            {normalized.map((option) => (
              <li key={option.value}>
                <label className="bc-permissions-audit__col-filter-option">
                  <input
                    type="checkbox"
                    checked={selected.includes(option.value)}
                    onChange={() => toggleOption(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
